import { IDateControl } from "Controls/Interfaces";
import { ControlEventTypes } from "Core/Events/EventTypes";

import { CoreContext } from "Core";
import { BaseDateControl } from "Controls/BaseDateControl";

/**
 * ## DateTime
 *
 * Control for editing date and time values.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a DateTimeControl with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class DateTimeControl extends BaseDateControl implements IDateControl {
    private _kendoElement: kendo.ui.DateTimePicker;

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-datetime";

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this.jQueryElement.on("focusout", (e) => this.trigger(ControlEventTypes.LostFocus));
        this.jQueryElement.on("paste", (e) => this.onPaste(e));

        let options: kendo.ui.DateTimePickerOptions = {
            format: CoreContext.Environment.DateTimeFormat,
            culture: CoreContext.Environment.Culture,
            change: (e) => this.trigger(ControlEventTypes.Change),
            dateInput: true,
            max: new Date(2999, 31, 12),
            min: new Date(1000, 1, 1),
        };
        this._baseKendoElement = this._kendoElement = new kendo.ui.DateTimePicker(this.jQueryElement[0], options);

        // the kendo control adds disabled class for readonly pickers -> remove disabled class and set default.
        if (element.prop("readonly")) {
            const inputWrapper: JQuery = (<any>this._kendoElement)._inputWrapper;
            inputWrapper.removeClass("k-state-disabled").addClass("k-state-default");
        }
        (<any>this._kendoElement)._dateInput.setOptions({
            messages: {
                month: "mm",
                day: "dd",
                year: "yyyy",
                hour: "hh",
                minute: "mm",
            },
        });

        this._kendoElement.wrapper.on("focus", ".k-input", (e) => {
            setTimeout(() => {
                const input = this._kendoElement.wrapper.find(".k-input");
                if (input.is(":focus")) {
                    input.trigger("select");
                }
            });
        });

        this._kendoElement.wrapper.on("keydown", ".k-input", (e) => {
            if ($.isNumeric(e.key)) {
                this.changeFocusCount++;
                let selection = (<any>this._kendoElement)._dateInput.element[0].selectionStart;
                if ((selection == 6 && this.changeFocusCount === 4) || (selection != 6 && this.changeFocusCount === 2)) {
                    this.changeFocusCount = 0;
                    this.setNextFocus();
                }
            }
        });
    }

    /**
     * Set interval for values in time picker popup list in minutes.
     * @param interval Interval in minutes.
     * @since 4.0.0
     */
    public setInterval(interval: number) {
        this._kendoElement.setOptions({ interval: interval });
    }

    /**
     * Set the minimum date for the datepicker.
     * @param date Minimum date.
     * @since 4.0.0
     */
    public setMin(date: Date): void {
        this._kendoElement.min(date);
    }

    /**
     * Get the minimum date for the datepicker.
     * @since 4.0.0
     */
    public getMin(): Date {
        return this._kendoElement.min();
    }

    /**
     * Set the maximum date for the datepicker.
     * @param date Maximum date.
     * @since 4.0.0
     */
    public setMax(date: Date): void {
        this._kendoElement.max(date);
    }

    /**
     * Get the maximum date for the datepicker.
     * @since 4.0.0
     */
    public getMax(): Date {
        return this._kendoElement.max();
    }

    /**
     * Set the control value.
     * @param value Value as Date or string.
     * @since 4.0.0
     */
    public setValue(value: Date | string): void {
        let oldValue = this.getValue();
        let newValue: Date = this.parseValue(value);

        if (oldValue !== newValue) {
            this._kendoElement.value(newValue);
            this._kendoElement.trigger("change");
        }
    }

    /**
     * Get control value.
     * @since 4.0.0
     */
    public getValue(): Date {
        return this._kendoElement.value();
    }

    /**
     * Get the kendo datepicker control. Click {@link http://docs.telerik.com/kendo-ui/api/javascript/ui/datetimepicker|here} for the api definition.
     * @since 4.0.0
     */
    public getKendoElement(): kendo.ui.DateTimePicker {
        return this._kendoElement;
    }

    /**
     * Gets the display template for formatting in grid row.
     * @hidden
     */
    protected getDisplayTemplate(bindingField?: string): string {
        return `#= ${bindingField} == null ? "${
            this.controlDefinition.ReplaceNullValue || ""
        }"  : kendo.toString(new Date(${bindingField}), "${CoreContext.Environment.DateTimeFormat} ") # `;
    }

    /**
     * Enable the control.
     * @since 4.0.0
     */
    public enable(): void {
        this._kendoElement.enable(true);
    }

    /**
     * Disable the control.
     * @since 4.0.0
     */
    public disable(): void {
        this._kendoElement.enable(false);
    }

    /**
     * Returns true if control is enabled.
     * @since 4.0.0
     */
    public isEnabled(): boolean {
        return !this._kendoElement.element.prop("disabled");
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.0.0
     * @hidden
     */
    public getOuterElement(): JQuery {
        return this._kendoElement.wrapper;
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this._kendoElement != null) {
            this._kendoElement.destroy();
        }
        if (this.jQueryElement != null) {
            this.jQueryElement.off("focusout");
            this.jQueryElement.off("paste");
        }
    }
}
